/**
 * Native crash reporting + basic analytics.
 *
 * Two sinks, both native-only and both fail-soft:
 *  1. Firebase — Crashlytics (crashes / non-fatal errors) and Analytics
 *     (screen views + events), the standard store-release monitoring stack.
 *  2. Supabase — `native_app_sessions` / `native_app_events` /
 *     `native_app_errors` via the `native-telemetry` edge function, so sessions
 *     and errors are also visible inside HIBANE Desk without the Firebase console.
 *
 * On the web every function here is a no-op: nothing is loaded, nothing is sent.
 */
import { supabase } from "@/integrations/supabase/client";
import { getPlatform, isNativeApp } from "./platform";
import { getInstalledAppInfo } from "./appUpdate";

const FLUSH_INTERVAL_MS = 15000;
const MAX_QUEUE = 200;

type EventRow = {
  event_name: string;
  screen?: string | null;
  properties?: Record<string, unknown>;
  occurred_at: string;
};

type ErrorRow = {
  severity: "warning" | "error" | "fatal";
  kind: string;
  message: string;
  stack?: string | null;
  route?: string | null;
  context?: Record<string, unknown>;
  occurred_at: string;
};

interface TelemetryState {
  sessionKey: string;
  userId: string | null;
  appVersion: string | null;
  appBuild: number | null;
  screenViews: number;
  events: EventRow[];
  errors: ErrorRow[];
  timer: number | null;
  started: boolean;
}

const state: TelemetryState = {
  sessionKey: "",
  userId: null,
  appVersion: null,
  appBuild: null,
  screenViews: 0,
  events: [],
  errors: [],
  timer: null,
  started: false,
};

const newSessionKey = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const base = () => ({
  platform: getPlatform(),
  session_key: state.sessionKey,
  user_id: state.userId,
  app_version: state.appVersion,
  app_build: state.appBuild,
});

const post = async (body: Record<string, unknown>) => {
  try {
    await supabase.functions.invoke("native-telemetry", { body });
  } catch {
    /* telemetry must never break the app */
  }
};

/* ------------------------------ Firebase sinks ----------------------------- */

const crashlytics = async () => {
  try {
    const mod = await import("@capacitor-firebase/crashlytics");
    return mod.FirebaseCrashlytics;
  } catch {
    return null;
  }
};

const analytics = async () => {
  try {
    const mod = await import("@capacitor-firebase/analytics");
    return mod.FirebaseAnalytics;
  } catch {
    return null;
  }
};

/* ------------------------------- Public API ------------------------------- */

/** Attach (or clear) the signed-in admin so crashes and sessions are attributable. */
export const setTelemetryUser = async (userId: string | null): Promise<void> => {
  if (!isNativeApp()) return;
  state.userId = userId;
  const [c, a] = await Promise.all([crashlytics(), analytics()]);
  try {
    await c?.setUserId({ userId: userId ?? "" });
    await a?.setUserId({ userId: userId ?? undefined });
  } catch {
    /* ignore */
  }
  if (state.started) await sendSessionPing();
};

/** Upsert the current session row (start + heartbeat). */
export const sendSessionPing = async (): Promise<void> => {
  if (!isNativeApp() || !state.sessionKey) return;
  await post({
    type: "session",
    ...base(),
    os_version: navigator.userAgent.slice(0, 60),
    device_model: navigator.platform || null,
    portal: "admin",
    screen_views: state.screenViews,
  });
};

/** Record a screen view (Firebase Analytics + Supabase). */
export const trackScreen = (screen: string): void => {
  if (!isNativeApp()) return;
  state.screenViews += 1;
  state.events.push({
    event_name: "screen_view",
    screen,
    properties: {},
    occurred_at: new Date().toISOString(),
  });
  if (state.events.length > MAX_QUEUE) state.events.splice(0, state.events.length - MAX_QUEUE);
  void (async () => {
    const a = await analytics();
    try {
      await a?.setCurrentScreen({ screenName: screen });
      await a?.logEvent({ name: "screen_view", params: { screen_name: screen } });
    } catch {
      /* ignore */
    }
    const c = await crashlytics();
    try {
      await c?.log({ message: `screen: ${screen}` });
    } catch {
      /* ignore */
    }
  })();
};

/** Record a named analytics event. */
export const trackEvent = (
  name: string,
  properties: Record<string, unknown> = {},
): void => {
  if (!isNativeApp()) return;
  state.events.push({
    event_name: name,
    properties,
    occurred_at: new Date().toISOString(),
  });
  if (state.events.length > MAX_QUEUE) state.events.splice(0, state.events.length - MAX_QUEUE);
  void (async () => {
    const a = await analytics();
    try {
      await a?.logEvent({ name, params: properties as Record<string, string | number | boolean> });
    } catch {
      /* ignore */
    }
  })();
};

/** Record a non-fatal error / crash report. */
export const trackError = (
  error: unknown,
  options: {
    kind?: string;
    severity?: "warning" | "error" | "fatal";
    context?: Record<string, unknown>;
  } = {},
): void => {
  if (!isNativeApp()) return;
  const err = error instanceof Error ? error : new Error(String(error));
  state.errors.push({
    severity: options.severity ?? "error",
    kind: options.kind ?? "js_error",
    message: err.message || "Unknown error",
    stack: err.stack ?? null,
    route: `${window.location.pathname}${window.location.hash}`,
    context: options.context ?? {},
    occurred_at: new Date().toISOString(),
  });
  if (state.errors.length > 50) state.errors.splice(0, state.errors.length - 50);

  void (async () => {
    const c = await crashlytics();
    try {
      await c?.recordException({ message: err.message, stacktrace: undefined });
    } catch {
      /* ignore */
    }
  })();

  // Errors are worth sending promptly rather than waiting for the timer.
  void flushTelemetry();
};

/** Send anything queued. Safe to call at any time. */
export const flushTelemetry = async (): Promise<void> => {
  if (!isNativeApp() || !state.sessionKey) return;
  const events = state.events.splice(0, state.events.length);
  const errors = state.errors.splice(0, state.errors.length);
  const jobs: Promise<void>[] = [];
  if (events.length) jobs.push(post({ type: "events", ...base(), events }));
  if (errors.length) jobs.push(post({ type: "errors", ...base(), errors }));
  await Promise.all(jobs);
};

/**
 * Start a telemetry session: enables Crashlytics/Analytics collection, records
 * the session row and installs global error handlers. Returns a cleanup fn.
 */
export const startTelemetrySession = async (): Promise<() => void> => {
  if (!isNativeApp()) return () => {};
  if (state.started) return () => {};
  state.started = true;
  state.sessionKey = newSessionKey();
  state.screenViews = 0;

  const info = await getInstalledAppInfo();
  state.appVersion = info.version;
  state.appBuild = info.build;

  const [c, a] = await Promise.all([crashlytics(), analytics()]);
  try {
    await c?.setEnabled({ enabled: true });
    await a?.setEnabled({ enabled: true });
    await c?.setCustomKey({ key: "app_build", value: String(info.build ?? ""), type: "string" });
  } catch {
    /* ignore */
  }

  await sendSessionPing();
  trackEvent("app_session_start", { version: info.version ?? "", build: info.build ?? 0 });

  const onError = (e: ErrorEvent) => {
    trackError(e.error ?? e.message, { kind: "window_error", context: { source: e.filename } });
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    trackError(e.reason, { kind: "unhandled_rejection" });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  state.timer = window.setInterval(() => {
    void flushTelemetry();
    void sendSessionPing();
  }, FLUSH_INTERVAL_MS);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    if (state.timer !== null) window.clearInterval(state.timer);
    state.timer = null;
    state.started = false;
    void flushTelemetry();
  };
};
